import * as React from "react";
import { formatPhoneAsUserTypes, nextPhoneInput } from "@servicepro/utils";
import type { VariantProps } from 'class-variance-authority';
import { cn } from "../../lib/utils";
import { cva } from 'class-variance-authority';

const inputVariants = cva(
    "jobsite:file:text-foreground jobsite:placeholder:text-muted-foreground jobsite:selection:bg-primary jobsite:selection:text-primary-foreground jobsite:dark:bg-input/30 jobsite:border-input jobsite:h-9 jobsite:w-full jobsite:min-w-0 jobsite:border jobsite:bg-transparent jobsite:px-3 jobsite:py-1 jobsite:text-base jobsite:transition-[color,box-shadow] jobsite:outline-none jobsite:file:inline-flex jobsite:file:h-7 jobsite:file:border-0 jobsite:file:bg-transparent jobsite:file:text-sm jobsite:file:font-medium jobsite:disabled:pointer-events-none jobsite:disabled:cursor-not-allowed jobsite:disabled:opacity-50 jobsite:md:text-sm jobsite:focus-visible:border-ring jobsite:focus-visible:ring-ring/50 jobsite:focus-visible:ring-[3px] jobsite:aria-invalid:ring-destructive/20 jobsite:dark:aria-invalid:ring-destructive/40 jobsite:aria-invalid:border-destructive",
    {
      defaultVariants: {
        variant: "default",
      },
      variants: {
        variant: {
          default: "",
          rounded: "jobsite:rounded-md",
        },
      },
    },
  ),

  Input = ({
    className,
    type,
    variant = "default",
    ...props
  }: React.ComponentProps<"input"> & VariantProps<typeof inputVariants>) => (
    <input
      type={type}
      data-slot="input"
      className={cn(inputVariants({ className, variant }))}
      style={{ borderRadius: "var(--jobsite-radius)" }}
      {...props}
    />
  ),

  /**
   * A phone field that formats the number as the customer types it, the way
   * the app's phone fields do: "7742456079" shows as "(774) 245-6079".
   *
   * @param {object} props The input's props, with the number as text.
   * @param {string} props.value The number so far, in any shape.
   * @param {(phone: string) => void} props.onValueChange Takes the number as formatted.
   * @returns {React.JSX.Element} The field.
   */
  PhoneInput = ({
    value,
    onValueChange,
    ...props
  }: Omit<React.ComponentProps<typeof Input>, "onChange" | "type" | "value"> & {
    value: string;
    onValueChange: (phone: string) => void;
  }): React.JSX.Element => {
    const shown = formatPhoneAsUserTypes(value);
    return (
      <Input
        autoComplete="tel"
        inputMode="tel"
        {...props}
        type="tel"
        value={shown}
        onChange={(event) => {
          onValueChange(nextPhoneInput(event.target.value, shown));
        }}
      />
    );
  };

export { Input, PhoneInput };
